import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, LogOut, Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const { user, loading: authLoading, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
  }, [location]);

  const navItems = [
    { to: '/', label: t('home') },
    { to: '/about', label: t('about') },
    { to: '/services', label: t('services') },
    { to: '/ecosystem', label: t('ecosystem') },
    { to: '/contact', label: t('contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#050505]/90 backdrop-blur-md shadow-[0_1px_0_0_rgba(255,255,255,0.07)]' : 'bg-[#050505]/70 backdrop-blur-sm'
        }`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        data-testid="header"
      >
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-[72px]">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-3 group"
              data-testid="header-logo-link"
            >
              <img
                src="/cexus-logo.png"
                alt="CEXUS"
                className="w-8 h-8 md:w-9 md:h-9 object-contain"
                style={{ filter: 'brightness(1.9)' }}
              />
              <span
                className="text-sm md:text-base font-semibold tracking-[0.15em] font-['Syne']"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 30%, #e2c179, #afa283)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                CEXUS
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`nav-link ${isActive(item.to) ? 'active' : ''}`}
                  data-testid={`nav-${item.to.replace('/', '') || 'home'}`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right section */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className="lang-toggle"
                data-testid="language-toggle"
              >
                <span className={language === 'en' ? 'text-white font-semibold' : ''}>EN</span>
                <span className="text-gray-600">|</span>
                <span className={language === 'fr' ? 'text-white font-semibold' : ''}>FR</span>
              </button>

              {!authLoading && !user && (
                <Link
                  to="/auth"
                  className="hidden md:inline-flex items-center text-xs font-semibold tracking-wide px-4 py-2 rounded-full border border-white/15 text-white hover:bg-white/10 transition-colors"
                  data-testid="header-signin-btn"
                >
                  {t('navSignIn')}
                </Link>
              )}

              {user && (
                <div className="relative hidden md:block">
                  <button
                    onClick={() => setProfileOpen((o) => !o)}
                    className="flex items-center"
                    data-testid="header-avatar-btn"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/20" referrerPolicy="no-referrer" />
                    ) : (
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-black text-xs font-bold"
                        style={{ background: 'linear-gradient(135deg, #706958, #e2c179)' }}
                      >
                        {(user.name || user.email || '?')[0].toUpperCase()}
                      </div>
                    )}
                  </button>
                  <AnimatePresence>
                    {profileOpen && (
                      <motion.div
                        className="absolute right-0 top-11 w-56 rounded-xl border border-white/10 p-2 shadow-2xl"
                        style={{ background: 'rgba(10,10,10,0.96)', backdropFilter: 'blur(20px)' }}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        data-testid="header-profile-menu"
                      >
                        <div className="px-3 py-2 border-b border-white/10 mb-1">
                          <p className="text-sm font-semibold text-white truncate">{user.name}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                        <Link
                          to="/dashboard"
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-300 hover:bg-white/[0.06] hover:text-white transition-colors"
                          data-testid="header-dashboard-link"
                        >
                          <LayoutDashboard size={15} />
                          {t('navDashboard')}
                        </Link>
                        <button
                          onClick={() => { setProfileOpen(false); logout(); }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-300 hover:bg-white/[0.06] hover:text-white transition-colors"
                          data-testid="header-signout-btn"
                        >
                          <LogOut size={15} />
                          {t('navSignOut')}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 text-white"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="mobile-menu-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              className="mobile-menu-panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              data-testid="mobile-menu"
            >
              <div className="flex justify-end mb-8">
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 text-white"
                  data-testid="mobile-menu-close"
                >
                  <X size={20} />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {navItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`py-3 px-4 text-base font-medium transition-colors ${
                      isActive(item.to)
                        ? 'text-white bg-white/[0.06]'
                        : 'text-gray-500 hover:text-white hover:bg-white/[0.06]'
                    }`}
                    data-testid={`mobile-nav-${item.to.replace('/', '') || 'home'}`}
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="border-t border-white/10 mt-3 pt-3">
                  {user ? (
                    <>
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-2 py-3 px-4 text-base font-medium text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors"
                        data-testid="mobile-nav-dashboard"
                      >
                        <LayoutDashboard size={17} />
                        {t('navDashboard')}
                      </Link>
                      <button
                        onClick={() => { setMobileOpen(false); logout(); }}
                        className="w-full flex items-center gap-2 py-3 px-4 text-base font-medium text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors"
                        data-testid="mobile-signout-btn"
                      >
                        <LogOut size={17} />
                        {t('navSignOut')}
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/auth"
                      className="block w-full py-3 px-4 text-base font-medium text-left text-white bg-white/[0.06] rounded-lg"
                      data-testid="mobile-signin-btn"
                    >
                      {t('navSignIn')}
                    </Link>
                  )}
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
